import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/config';

export interface OnboardingData {
  userId: string;
  publicationName: string;
  publicationDescription: string;
  targetAudience: string;
  contentCategories: string[];
  publishingFrequency: string;
  theme: {
    primaryColor: string;
    fontFamily: string;
  };
  goals: string[];
  completedAt: Date;
  completedSteps: string[];
}

export interface UserProfile {
  userId: string;
  displayName: string;
  email: string;
  photoURL?: string;
  publicationName: string;
  publicationDescription: string;
  targetAudience: string;
  contentCategories: string[];
  publishingFrequency: string;
  theme: {
    primaryColor: string;
    fontFamily: string;
  };
  goals: string[];
  preferences: {
    notifications: boolean;
    weeklyDigest: boolean;
    readingTime: string;
    queueLimit: number;
  };
  createdAt: Date;
  updatedAt: Date;
}

// Store onboarding data
export const saveOnboardingData = async (userId: string, data: Partial<OnboardingData>): Promise<void> => {
  try {
    const onboardingRef = doc(collection(db, 'onboarding'), userId);
    const onboardingData: OnboardingData = {
      userId,
      publicationName: data.publicationName || '',
      publicationDescription: data.publicationDescription || '',
      targetAudience: data.targetAudience || '',
      contentCategories: data.contentCategories || [],
      publishingFrequency: data.publishingFrequency || 'weekly',
      theme: data.theme || { primaryColor: '#667eea', fontFamily: 'Inter' },
      goals: data.goals || [],
      completedAt: new Date(),
      completedSteps: data.completedSteps || []
    };
    
    await setDoc(onboardingRef, onboardingData);
    
    // Also create/update user profile
    await updateUserProfile(userId, {
      publicationName: onboardingData.publicationName,
      publicationDescription: onboardingData.publicationDescription,
      targetAudience: onboardingData.targetAudience,
      contentCategories: onboardingData.contentCategories,
      publishingFrequency: onboardingData.publishingFrequency,
      theme: onboardingData.theme,
      goals: onboardingData.goals
    });
  } catch (error) {
    console.error('Error saving onboarding data:', error);
    throw error;
  }
};

// Get onboarding data
export const getOnboardingData = async (userId: string): Promise<OnboardingData | null> => {
  try {
    const onboardingRef = doc(collection(db, 'onboarding'), userId);
    const docSnap = await getDoc(onboardingRef);
    
    if (docSnap.exists()) {
      return { ...docSnap.data(), completedAt: docSnap.data().completedAt.toDate() } as OnboardingData;
    }
    return null;
  } catch (error) {
    console.error('Error getting onboarding data:', error);
    return null;
  }
};

// Create or update user profile
export const updateUserProfile = async (userId: string, profileData: Partial<UserProfile>): Promise<void> => {
  try {
    const profileRef = doc(collection(db, 'userProfiles'), userId);
    const existingDoc = await getDoc(profileRef);
    
    const profile: UserProfile = {
      userId,
      displayName: profileData.displayName || '',
      email: profileData.email || '',
      photoURL: profileData.photoURL,
      publicationName: profileData.publicationName || '',
      publicationDescription: profileData.publicationDescription || '',
      targetAudience: profileData.targetAudience || '',
      contentCategories: profileData.contentCategories || [],
      publishingFrequency: profileData.publishingFrequency || 'weekly',
      theme: profileData.theme || { primaryColor: '#667eea', fontFamily: 'Inter' },
      goals: profileData.goals || [],
      preferences: profileData.preferences || {
        notifications: true,
        weeklyDigest: true,
        readingTime: 'evening',
        queueLimit: 100
      },
      createdAt: existingDoc.exists() ? existingDoc.data().createdAt.toDate() : new Date(),
      updatedAt: new Date()
    };
    
    await setDoc(profileRef, profile);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Get user profile
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const profileRef = doc(collection(db, 'userProfiles'), userId);
    const docSnap = await getDoc(profileRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...data,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate()
      } as UserProfile;
    }
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
};

// Check if user has completed onboarding
export const hasCompletedOnboarding = async (userId: string): Promise<boolean> => {
  try {
    const onboardingData = await getOnboardingData(userId);
    return onboardingData !== null;
  } catch (error) {
    console.error('Error checking onboarding status:', error);
    return false;
  }
};

// Get onboarding completion status
export const getOnboardingStatus = async (userId: string): Promise<{
  completed: boolean;
  completedSteps: string[];
  progress: number;
}> => {
  try {
    const onboardingData = await getOnboardingData(userId);
    
    if (!onboardingData) {
      return {
        completed: false,
        completedSteps: [],
        progress: 0
      };
    }
    
    const totalSteps = 4; // Welcome, Audience, Goals, Theme
    const progress = (onboardingData.completedSteps.length / totalSteps) * 100;
    
    return {
      completed: onboardingData.completedSteps.length === totalSteps,
      completedSteps: onboardingData.completedSteps,
      progress
    };
  } catch (error) {
    console.error('Error getting onboarding status:', error);
    return {
      completed: false,
      completedSteps: [],
      progress: 0
    };
  }
};
